import "../dataset-style.scss";

import {
  CAlert,
  CBadge,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
} from "@coreui/react";
import React, { useEffect, useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaCloudUploadAlt,
  FaRedo,
  FaSpinner,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import StyledDropzone, { UploadToDatasetHeader } from "./Dropzone";

import { PropTypes } from "prop-types";
import DataTable from "react-data-table-component";
import { useParams } from "react-router-dom";
import { Can } from "src/helpers/Auth";
import { NucToast } from "src/reusable/components/Toast";
import useDatasetViewerNavigation from "src/reusable/hooks/useDatasetViewerNavigation";
import { useUnifiedChunkedUpload } from "src/reusable/hooks/useUnifiedChunkedUpload";
import { formatFileSize } from "src/helpers/formatFileSize";

// Add spinning animation for the loading spinner
const spinnerStyle = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Inject the CSS into the document head
if (
  typeof document !== "undefined" &&
  !document.querySelector("#spinner-animation")
) {
  const style = document.createElement("style");
  style.id = "spinner-animation";
  style.textContent = spinnerStyle;
  document.head.appendChild(style);
}

/**
 * Removes the dataset name and truncates it if necessary.
 *
 * @param {string} name - The name of the dataset.
 * @returns {string} - The modified dataset name.
 */
const removeDatasetNameAndTruncate = (name) => {
  // Handle undefined or null names
  if (!name) {
    return "";
  }

  if (name.endsWith(".xer")) {
    let truncatedFileName;

    // Remove the ".xer" extension
    const fileNameWithoutExtension = name.replace(/\.xer$/, "");

    // Use a regular expression to remove the prefix
    const cleanedFileName = fileNameWithoutExtension.replace(
      /dataset_files\/dataset_id_\d+_/,
      "",
    );

    // Truncate the remaining text to 30 characters or less
    if (cleanedFileName.length > 40) {
      truncatedFileName = cleanedFileName.slice(0, 37) + "...";
    } else {
      truncatedFileName = cleanedFileName;
    }

    // Add the ".xer" extension back
    const finalFileName = truncatedFileName + ".xer";

    return finalFileName;
  } else return name;
};

/**
 * Renders a table of files with their details and actions.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.files - The array of files to display.
 * @param {Array} props.uploadQueue - The upload queue from useUnifiedChunkedUpload.
 * @param {Function} props.retryUpload - Function to retry a failed upload.
 * @param {Function} props.cancelUpload - Function to cancel an upload.
 * @param {Object} props.uploadResults - Map of file IDs to upload results.
 * @param {string} props.datasetId - The ID of the dataset.
 * @param {Function} props.deleteFunction - The function to delete a file.
 * @returns {JSX.Element} The rendered FilesTable component.
 */
export const FilesTable = ({
  files,
  uploadQueue,
  retryUpload,
  cancelUpload, // eslint-disable-line no-unused-vars
  uploadResults,
  datasetId,
  deleteFunction,
}) => {
  const columns = [
    {
      name: "File Name",
      selector: (row) => {
        // Simple file name extraction
        let fileName = row.name || "Unknown";

        let rowName = removeDatasetNameAndTruncate(fileName);
        const uploadResult = uploadResults[row.uniqueId];
        const newRecordId = uploadResult?.data?.[1]?.id;

        // If we have a new record ID from upload, show as link
        if (newRecordId) {
          return (
            <a
              href={`/datasets/${datasetId}/document/${newRecordId}`}
              target="_blank"
              rel="noreferrer"
              title={fileName} // Show full name on hover
            >
              {rowName}
            </a>
          );
        }
        // Otherwise just show the name
        return <span title={fileName}>{rowName}</span>;
      },
      sortable: true,
      grow: 2,
    },
    {
      name: "Type",
      selector: (row) => {
        // Extract file extension
        const extension = row.name
          ? row.name.split(".").pop()?.toUpperCase()
          : "";
        // Get MIME type
        const mimeType = row.type || "";

        // Format the display
        if (extension) {
          return <span title={mimeType || "Unknown type"}>{extension}</span>;
        }
        return "N/A";
      },
      sortable: true,
      width: "80px",
    },
    {
      name: "File Size",
      selector: (row) => {
        // Handle both formatted and raw size
        if (row.sizeFormatted) {
          return row.sizeFormatted;
        } else if (row.size !== undefined && row.size !== null) {
          return formatFileSize(row.size);
        }
        return "N/A";
      },
      sortable: true,
      omit: false,
      maxWidth: "150px",
      grow: 1,
    },
    {
      name: "Last Modified Date",
      selector: (row) =>
        String(
          row.lastModifiedDate
            ? new Date(row.lastModifiedDate).toLocaleString("en-GB", {
                timeZone: "UTC",
              })
            : row.create_date
              ? new Date(row.create_date).toLocaleString("en-GB", {
                  timeZone: "UTC",
                })
              : "N/A",
        ),
      sortable: true,
      grow: 1,
    },
    {
      name: "Status",
      width: "150px",
      cell: (row) => {
        const queueItem = uploadQueue?.find((item) => item.id === row.uniqueId);
        const uploadResult = uploadResults[row.uniqueId];

        return (
          <UploadCellNew
            row={row}
            queueItem={queueItem}
            uploadResult={uploadResult}
            retryUpload={retryUpload}
          />
        );
      },
    },
    {
      name: "Pages",
      selector: (row) => {
        // This would need backend support to detect page count
        const uploadResult = uploadResults[row.uniqueId];
        if (uploadResult?.data?.[1]?.page_count) {
          return uploadResult.data[1].page_count;
        }
        // Check if it's a PDF file
        if (
          row.type === "application/pdf" ||
          row.name?.toLowerCase().endsWith(".pdf")
        ) {
          return <span style={{ color: "#6c757d" }}>PDF</span>;
        }
        return "-";
      },
      width: "70px",
      center: true,
    },
    {
      name: "Actions",
      width: "80px",
      compact: true,
      omit: !deleteFunction,
      cell: (row) => {
        const uploadResult = uploadResults[row.uniqueId];
        const newRecordId = uploadResult?.data?.[1]?.id;

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "10px",
              width: "100%",
            }}
          >
            {newRecordId && (
              <a
                href={`/datasets/${datasetId}/document/${newRecordId}`}
                target="_blank"
                rel="noreferrer"
                title="View Document"
              >
                <i
                  className="fa fa-eye"
                  style={{ cursor: "pointer", color: "#007bff" }}
                />
              </a>
            )}
            <FaTrash
              className="document-upload-delete"
              style={{ cursor: "pointer", color: "#dc3545" }}
              onClick={() => deleteFunction(row.id || row.uniqueId, row)}
              title="Delete File"
            />
          </div>
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={files}
      pagination={true}
      paginationPerPage={25}
      paginationRowsPerPageOptions={[10, 25, 50, 100]}
      paginationComponentOptions={{
        rowsPerPageText: "Files per page:",
        rangeSeparatorText: "of",
        noRowsPerPage: false,
        selectAllRowsItem: true,
        selectAllRowsItemText: "All",
      }}
    />
  );
};

FilesTable.propTypes = {
  files: PropTypes.array,
  uploadQueue: PropTypes.array,
  retryUpload: PropTypes.func,
  cancelUpload: PropTypes.func,
  uploadResults: PropTypes.object,
  datasetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  deleteFunction: PropTypes.func,
};

/**
 * Renders a cell showing upload status.
 */
const UploadCellNew = ({ row, queueItem, uploadResult, retryUpload }) => {
  const status = queueItem?.status || (uploadResult ? "completed" : "idle");
  const progress = queueItem?.progress || 0;
  const error = queueItem?.error;

  // Pre-uploaded files (have storage_id or id from server)
  if (!queueItem && !uploadResult && (row.storage_id || row.id)) {
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <FaCheckCircle style={{ color: "green", fontSize: "2em" }} />
        <span style={{ marginLeft: "8px", color: "green" }}>Uploaded</span>
      </div>
    );
  }

  // Uploaded successfully
  if (status === "completed" || uploadResult) {
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <FaCheckCircle style={{ color: "green", fontSize: "2em" }} />
        <span style={{ marginLeft: "8px", color: "green" }}>Uploaded</span>
      </div>
    );
  }

  // Currently uploading
  if (status === "uploading") {
    return (
      <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
        {progress > 0 ? (
          <>
            <progress
              max="100"
              value={progress}
              style={{
                width: "80px",
                height: "10px",
                marginRight: "8px",
              }}
            />
            <span style={{ fontSize: "0.9em" }}>{progress.toFixed(0)}%</span>
          </>
        ) : (
          <>
            <FaSpinner
              style={{
                color: "#007bff",
                fontSize: "1.8em",
                animation: "spin 1s linear infinite",
              }}
            />
            <span style={{ marginLeft: "8px" }}>Uploading...</span>
          </>
        )}
      </div>
    );
  }

  // Failed upload
  if (status === "failed" || error) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
        <FaTimes style={{ color: "red", fontSize: "1.8em" }} />
        <span style={{ color: "red", fontSize: "0.9em" }}>Failed</span>
        <FaRedo
          style={{
            color: "#007bff",
            fontSize: "1.5em",
            cursor: "pointer",
            marginLeft: "5px",
          }}
          onClick={() => retryUpload(row.uniqueId)}
          title="Retry upload"
        />
      </div>
    );
  }

  // Pending/queued
  if (status === "pending") {
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <FaSpinner
          style={{
            color: "#6c757d",
            fontSize: "1.5em",
          }}
        />
        <span style={{ marginLeft: "8px", color: "#6c757d" }}>Queued</span>
      </div>
    );
  }

  // Idle state (shouldn't happen with autoUpload)
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <span style={{ color: "#6c757d" }}>Ready</span>
    </div>
  );
};

UploadCellNew.propTypes = {
  row: PropTypes.object,
  queueItem: PropTypes.object,
  uploadResult: PropTypes.object,
  retryUpload: PropTypes.func,
};

const MultiFileUploader = (props) => {
  const [displayFiles, setDisplayFiles] = React.useState([]);
  const [fileUploadFailed, setFileUploadFailed] = React.useState(false);
  const [autoUpload, setAutoUpload] = useState(true);
  const [deleteName, setDeleteName] = useState(null);
  const [uploadResults, setUploadResults] = useState({});

  let { dataset_id: datasetId } = useParams();

  if (props.datasetId) {
    datasetId = props.datasetId;
  }
  // Use the unified chunked upload hook for proper concurrent limiting
  const {
    files,
    uploadError,
    uploadQueue,
    handleFileUpload,
    removeFile,
    cancelUpload,
    retryUpload,
  } = useUnifiedChunkedUpload({
    context: props.context ?? "dataset",
    contextId: datasetId,
    maxConcurrent: 10, // Will be overridden by server limit
    autoUpload: autoUpload && Boolean(datasetId),
    onUploadComplete: (file, result) => {
      setUploadResults((prev) => ({
        ...prev,
        [file.uniqueId]: result,
      }));
    },
  });

  // Merge uploaded files from props with files from hook
  useEffect(() => {
    if (props.uploadedFiles && props.uploadedFiles.length > 0) {
      // Convert prop files to match our format
      const convertedFiles = props.uploadedFiles.map((file) => ({
        ...file,
        uniqueId: file.uid || file.id || `file-${Date.now()}-${Math.random()}`,
        sizeFormatted: formatFileSize(file.size),
      }));
      setDisplayFiles(convertedFiles);
    }
  }, [props.uploadedFiles]);

  // Update display files when files from hook change
  useEffect(() => {
    if (files.length > 0) {
      setDisplayFiles(files);
    }
  }, [files]);

  const navigateToDataset = useDatasetViewerNavigation({
    datasetId: datasetId,
    datasetFolderId: props.dataset?.dataset_folder_id,
  });

  const handleDrop = (droppedFiles) => {
    // Ensure we have proper File objects
    const properFiles = droppedFiles.map((file) => {
      // If it's already a File object, use it directly
      if (file instanceof File) {
        return file;
      }
      // If it has an originFileObj property (from Ant Design Upload), use that
      if (file.originFileObj instanceof File) {
        return file.originFileObj;
      }
      // Otherwise, try to extract the file
      if (file.file instanceof File) {
        return file.file;
      }
      // Log unexpected structure
      console.warn("Unexpected file structure in handleDrop:", file);
      return file;
    });

    // Check for duplicates using the proper file names
    const filteredFiles = properFiles.filter((file) => {
      const fileName = file.name || file.fileName || "unknown";
      return !displayFiles.some((displayFile) => {
        const displayName = displayFile.name || displayFile.fileName || "";
        return displayName === fileName;
      });
    });

    if (filteredFiles.length < properFiles.length) {
      setFileUploadFailed(true);
      NucToast({
        message: "Duplicate file, please ensure unique file upload.",
        type: "error",
      });
    }

    if (filteredFiles.length > 0) {
      handleFileUpload(filteredFiles);
    }
  };

  const handleDelete = (fileId, row) => {
    // Find index by uniqueId
    const fileIndex = displayFiles.findIndex((f) => f.uniqueId === fileId);
    if (fileIndex !== -1) {
      removeFile(fileIndex);
    }

    // Also handle prop delete function if provided
    if (props.deleteFunction && row.id) {
      props.deleteFunction(row.id);
      setDeleteName(row.name);
    }
  };

  // Calculate upload stats
  const stats = useMemo(() => {
    const uploading =
      uploadQueue?.filter((q) => q.status === "uploading").length || 0;
    const pending =
      uploadQueue?.filter((q) => q.status === "pending").length || 0;
    const completed =
      uploadQueue?.filter((q) => q.status === "completed").length || 0;
    const failed =
      uploadQueue?.filter((q) => q.status === "failed").length || 0;
    const total = uploadQueue?.length || 0;

    return { uploading, pending, completed, failed, total };
  }, [uploadQueue]);

  return (
    <CCard>
      {props.softDeleteProjectFileStatus === "success" && deleteName && (
        <CAlert color="success">Successfully Soft Deleted: {deleteName}</CAlert>
      )}
      {props.softDeleteProjectFileStatus === "error" && (
        <CAlert color="error">Failed Soft Deletion: {deleteName}</CAlert>
      )}
      {uploadError && <CAlert color="warning">{uploadError}</CAlert>}
      {props.titleHide ? (
        <></>
      ) : (
        <UploadToDatasetHeader
          showAutoUploadSwitch={true}
          isAutoUpload={autoUpload}
          handleIsAutoUpload={(e) => {
            setAutoUpload(e);
          }}
        />
      )}
      <Can
        perform="dataset:write"
        yes={() => (
          <div style={{ minHeight: "100px" }}>
            <CCardBody>
              <CRow>
                {fileUploadFailed && (
                  <CAlert color="danger">
                    Failed to upload some document files. Please ensure there
                    are no duplicates
                  </CAlert>
                )}
                <CCol md="12">
                  <StyledDropzone
                    acceptsMultipleFiles={true}
                    showFileAcceptedMessage={false}
                    showFileRejectionMessage={false}
                    acceptTypeString={props.fileType}
                    titleMessage={`Drag and drop or click to upload documents.`}
                    detailMessage={
                      props.context === "project-genius"
                        ? "Please upload a .xer file."
                        : "Please upload document files. We accept many common file types such as PDF, text, Microsoft Word, etc..."
                    }
                    onDragSuccess={handleDrop}
                    onDragFailed={() => {
                      NucToast({
                        message:
                          "Failed to upload file. Please ensure file type is accepted.",
                        type: "error",
                      });
                    }}
                  />
                </CCol>
              </CRow>

              {displayFiles && displayFiles.length > 0 && (
                <>
                  {/* Upload Status Dashboard - CoreUI styled */}
                  <CRow style={{ marginTop: "15px", marginBottom: "20px" }}>
                    <CCol>
                      <CCard>
                        <CCardHeader className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>Upload Status Dashboard</strong>
                            {stats.total > 0 && (
                              <CBadge color="primary" className="ml-2">
                                {stats.total} Total Files
                              </CBadge>
                            )}
                          </div>
                          {stats.total > 0 && (
                            <small className="text-muted">
                              {Math.round(
                                (stats.completed / stats.total) * 100,
                              )}
                              % Complete
                            </small>
                          )}
                        </CCardHeader>
                        <CCardBody>
                          {/* Status Cards Grid */}
                          <CRow>
                            {/* Uploading */}
                            <CCol xs="6" sm="3">
                              <CCard
                                className="text-center mb-3"
                                style={{
                                  borderTop:
                                    stats.uploading > 0
                                      ? "3px solid #17a2b8"
                                      : "1px solid #d8dbe0",
                                }}
                              >
                                <CCardBody className="p-3">
                                  <div
                                    style={{
                                      fontSize: "24px",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    {stats.uploading > 0 ? (
                                      <FaSpinner
                                        style={{
                                          animation: "spin 1s linear infinite",
                                          color: "#17a2b8",
                                        }}
                                      />
                                    ) : (
                                      <FaCloudUploadAlt
                                        style={{ color: "#6c757d" }}
                                      />
                                    )}
                                  </div>
                                  <div className="h4 mb-0">
                                    {stats.uploading}
                                  </div>
                                  <small className="text-muted text-uppercase font-weight-bold">
                                    Uploading
                                  </small>
                                </CCardBody>
                              </CCard>
                            </CCol>

                            {/* Queued */}
                            <CCol xs="6" sm="3">
                              <CCard
                                className="text-center mb-3"
                                style={{
                                  borderTop:
                                    stats.pending > 0
                                      ? "3px solid #ffc107"
                                      : "1px solid #d8dbe0",
                                }}
                              >
                                <CCardBody className="p-3">
                                  <div
                                    style={{
                                      fontSize: "24px",
                                      marginBottom: "8px",
                                      color: "#ffc107",
                                    }}
                                  >
                                    ⏳
                                  </div>
                                  <div className="h4 mb-0">{stats.pending}</div>
                                  <small className="text-muted text-uppercase font-weight-bold">
                                    Queued
                                  </small>
                                </CCardBody>
                              </CCard>
                            </CCol>

                            {/* Completed */}
                            <CCol xs="6" sm="3">
                              <CCard
                                className="text-center mb-3"
                                style={{
                                  borderTop:
                                    stats.completed > 0
                                      ? "3px solid #28a745"
                                      : "1px solid #d8dbe0",
                                }}
                              >
                                <CCardBody className="p-3">
                                  <div
                                    style={{
                                      fontSize: "24px",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <FaCheckCircle
                                      style={{ color: "#28a745" }}
                                    />
                                  </div>
                                  <div className="h4 mb-0">
                                    {stats.completed}
                                  </div>
                                  <small className="text-muted text-uppercase font-weight-bold">
                                    Completed
                                  </small>
                                </CCardBody>
                              </CCard>
                            </CCol>

                            {/* Failed */}
                            <CCol xs="6" sm="3">
                              <CCard
                                className="text-center mb-3"
                                style={{
                                  borderTop:
                                    stats.failed > 0
                                      ? "3px solid #dc3545"
                                      : "1px solid #d8dbe0",
                                }}
                              >
                                <CCardBody className="p-3">
                                  <div
                                    style={{
                                      fontSize: "24px",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <FaTimes
                                      style={{
                                        color:
                                          stats.failed > 0
                                            ? "#dc3545"
                                            : "#6c757d",
                                      }}
                                    />
                                  </div>
                                  <div className="h4 mb-0">{stats.failed}</div>
                                  <small className="text-muted text-uppercase font-weight-bold">
                                    Failed
                                  </small>
                                </CCardBody>
                              </CCard>
                            </CCol>
                          </CRow>

                          {/* Status Message */}
                          {(stats.uploading > 0 ||
                            stats.pending > 0 ||
                            stats.failed > 0 ||
                            stats.completed === stats.total) && (
                            <CAlert
                              color={
                                stats.uploading > 0
                                  ? "info"
                                  : stats.failed > 0 &&
                                      stats.uploading === 0 &&
                                      stats.pending === 0
                                    ? "warning"
                                    : stats.completed === stats.total &&
                                        stats.total > 0
                                      ? "success"
                                      : "secondary"
                              }
                              className="mb-0 mt-3"
                            >
                              {stats.uploading > 0 ? (
                                <>
                                  <FaSpinner
                                    style={{
                                      animation: "spin 1s linear infinite",
                                      marginRight: "8px",
                                    }}
                                  />
                                  Processing {stats.uploading} file
                                  {stats.uploading > 1 ? "s" : ""}...
                                </>
                              ) : stats.pending > 0 ? (
                                <>
                                  ⏳ {stats.pending} file
                                  {stats.pending > 1 ? "s" : ""} waiting in
                                  queue
                                </>
                              ) : stats.total > 0 &&
                                stats.completed === stats.total ? (
                                <>✅ All uploads completed successfully!</>
                              ) : stats.failed > 0 &&
                                stats.uploading === 0 &&
                                stats.pending === 0 ? (
                                <>
                                  ⚠️ {stats.failed} upload
                                  {stats.failed > 1 ? "s" : ""} failed - click
                                  retry to try again
                                </>
                              ) : (
                                <>
                                  📁 {displayFiles.length} file
                                  {displayFiles.length > 1 ? "s" : ""} selected
                                </>
                              )}
                            </CAlert>
                          )}
                        </CCardBody>
                      </CCard>
                    </CCol>
                  </CRow>

                  <FilesTable
                    files={displayFiles}
                    uploadQueue={uploadQueue}
                    retryUpload={retryUpload}
                    cancelUpload={cancelUpload}
                    uploadResults={uploadResults}
                    datasetId={datasetId}
                    deleteFunction={props.deleteFunction ? handleDelete : null}
                  />

                  <CRow style={{ marginTop: "15px" }}>
                    <CCol md="12">
                      <button
                        className="btn btn-primary"
                        onClick={navigateToDataset}
                        data-attr="datasets:btn:view-dataset-after-upload"
                      >
                        View Dataset
                      </button>
                    </CCol>
                  </CRow>
                </>
              )}
            </CCardBody>
          </div>
        )}
        no={() => (
          <CCardBody>
            You do not have permission to upload data to this dataset.
          </CCardBody>
        )}
      />
    </CCard>
  );
};

MultiFileUploader.propTypes = {
  titleHide: PropTypes.bool,
  datasetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dataset: PropTypes.object,
  onDoneClick: PropTypes.func,
  doneCallback: PropTypes.func,
  fileType: PropTypes.string,
  onChange: PropTypes.func,
  uploadedFiles: PropTypes.arrayOf(PropTypes.object),
  deleteFunction: PropTypes.func,
  softDeleteProjectFileStatus: PropTypes.string,
  context: PropTypes.string,
};

export default MultiFileUploader;
